import React, { useState, useEffect, useRef, useCallback } from 'react';
import BottomNav from '../../components/BottomNav';
import ScoreCard from '../../components/ScoreCard';
import { useGame, HINT_COSTS } from '../../context/GameContext';
import { SPEEDRUN_QS } from '../../data/gamesData';
import { buildQuestionPool } from '../../data/bibleData';
import { shuffle, shuffleOpts, PauseOverlay, HintBubble, ShareBtn, ScoreImageBtn, BibleRef, RulesBtn , xpLabel, xpShareLine, xpForCard , isAnswerCorrect } from './shared';
import { fetchQuestions } from '../../utils/questionsAPI';
import LoadingScreen from '../../components/LoadingScreen';

// ─── Pool local (fallback garanti) ─────────────────────────────────────────
// Appelé en synchrone pour constituer la base locale.
const buildLocalPool = () => {
  const base    = shuffle(SPEEDRUN_QS).map(shuffleOpts);
  const dynamic = buildQuestionPool(20).map(q => ({
    ...q,
    opts: q.opts ? shuffle(q.opts) : undefined,
  }));
  return shuffle([...base, ...dynamic]);
};

const SpeedrunGame = ({ onBack, onEnd }) => {
  const { profile } = useGame();

  // ── Phase : choose → ready → loading → running → done ──────────────────
  const [phase, setPhase]                   = useState('choose');
  const [correctionMode, setCorrectionMode] = useState('immediate');

  const [qs, setQs]             = useState([]);
  const [idx, setIdx]           = useState(0);
  const [input, setInput]       = useState('');
  const [score, setScore]       = useState(0);
  const [combo, setCombo]       = useState(0);
  const [timeLeft, setTimeLeft] = useState(60);
  const [maxCombo, setMaxCombo] = useState(0);
  const [flash, setFlash]       = useState(null);
  const [paused, setPaused]     = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showCorrection, setShowCorrection] = useState(null);
  const [history, setHistory]   = useState([]);
  const [showScoreCard, setShowScoreCard]   = useState(false);
  const [copied, setCopied]     = useState(false);
  // Indique la source des questions (pour info dans l'UI)
  const [apiCount, setApiCount] = useState(0);

  const timerRef     = useRef(null);
  const xpRef        = useRef(false);
  const hintsUsedRef = useRef(0);
  // fix V1 : XP confirmée par le serveur — undefined = en attente, null = échec.
  const [serverXp, setServerXp] = useState(undefined);
  // fix : jeton de partie — une réponse d'une partie précédente encore en vol
  // ne doit jamais alimenter l'écran de la partie courante.
  const sessionIdRef = useRef(0);

  const addFlash = useCallback((text, color) => {
    setFlash({ text, color });
    setTimeout(() => setFlash(null), 700);
  }, []);

  // ── Démarrage : fetch API (2 s max), merge avec local ─────────────────
  const start = useCallback(async () => {
    xpRef.current = false;
    sessionIdRef.current += 1;
    setServerXp(undefined);
    setPhase('loading');
    setApiCount(0);

    const { questions: apiQuestions } = await fetchQuestions({ n: 20 });
    setApiCount(apiQuestions.length);

    const local = buildLocalPool();
    // On mélange tout : questions locales + questions MongoDB
    const allQuestions = shuffle([...local, ...apiQuestions]);

    setQs(allQuestions);
    setIdx(0); setScore(0); setCombo(0); setTimeLeft(60); setMaxCombo(0);
    setHistory([]); setShowCorrection(null); setShowHint(false);
    setPhase('running');
  }, []);

  // ── Timer ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'running' || paused) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(timerRef.current); setPhase('done'); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase, paused]);

  // ── Validation d'une réponse ─────────────────────────────────────────
  const answer = useCallback((ans) => {
    if (showCorrection) return;
    const q  = qs[idx % qs.length];
    const ok = isAnswerCorrect(ans, String(q.a || q.opts?.[0] || ''), q.accept);
    setHistory(h => [...h, { q: q.q, a: q.a, given: ans, ok, ref: q.ref }]);

    if (ok) {
      const pts = 10 + combo * 5;
      setScore(s => s + pts);
      setCombo(c => { const n = c + 1; setMaxCombo(m => Math.max(m, n)); return n; });
      addFlash(`+${pts}`, '#40916c');
      setInput(''); setIdx(i => i + 1); setShowHint(false);
    } else {
      setCombo(0);
      addFlash('✗', '#e63946');
      if (correctionMode === 'immediate') {
        setShowCorrection({ q: q.q, correct: q.a, given: ans, ref: q.ref });
        setTimeout(() => { setShowCorrection(null); setInput(''); setIdx(i => i + 1); setShowHint(false); }, 2000);
      } else {
        setInput(''); setIdx(i => i + 1); setShowHint(false);
      }
    }
  }, [qs, idx, combo, addFlash, showCorrection, correctionMode]);

  const copyScore = () => {
    const correct = history.filter(h => h.ok).length;
    const text    = `⚡ Bible Speedrun — ${profile.name || 'Joueur'}\n🏆 Score : ${score} pts\n✅ ${correct}/${history.length} bonnes réponses\n🔥 Combo ×${maxCombo}${xpShareLine(serverXp)}\n🎮 Manna`;
    navigator.clipboard?.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  // ── CHOIX MODE CORRECTION ────────────────────────────────────────────
  if (phase === 'choose') return (
    <div className="page-content">
      <button className="btn btn-ghost" onClick={onBack}>← Retour</button>
      <div style={{ textAlign:'center', margin:'1.5rem 0' }}>
        <div style={{ fontSize:'4rem' }}>⚡</div>
        <h2 style={{ margin:'.5rem 0 .3rem' }}>Bible Speedrun</h2>
        <p className="text-small">60 secondes · Combos bonus · Questions variées</p>
      </div>

      <div className="card" style={{ marginBottom:'1.25rem' }}>
        <p style={{ fontFamily:'var(--font-display)', color:'var(--gold-light)', fontSize:'.9rem', marginBottom:'.75rem' }}>
          📋 Mode de correction
        </p>
        {[
          { key:'immediate', icon:'⚡', title:'Correction immédiate', desc:'Chaque mauvaise réponse affiche la bonne réponse pendant 2 secondes' },
          { key:'recap',     icon:'📝', title:'Résumé en fin de partie', desc:'Tu vois toutes tes erreurs uniquement à la fin' },
        ].map(({ key, icon, title, desc }) => (
          <div
            key={key}
            onClick={() => setCorrectionMode(key)}
            style={{
              display:'flex', alignItems:'flex-start', gap:'.75rem',
              padding:'.75rem', borderRadius:10, marginBottom:'.5rem', cursor:'pointer',
              background: correctionMode === key ? 'rgba(201,168,76,.15)' : 'rgba(255,255,255,.03)',
              border: `1.5px solid ${correctionMode === key ? 'rgba(201,168,76,.5)' : 'rgba(255,255,255,.06)'}`,
              transition:'all .2s',
            }}
          >
            <div style={{ fontSize:'1.4rem', flexShrink:0, marginTop:'.05rem' }}>{icon}</div>
            <div>
              <div style={{ fontFamily:'var(--font-display)', color:'var(--parch)', fontSize:'.9rem', fontWeight:700 }}>{title}</div>
              <div className="text-tiny" style={{ marginTop:'.2rem', lineHeight:1.4 }}>{desc}</div>
            </div>
            {correctionMode === key && (
              <div style={{ marginLeft:'auto', color:'var(--gold-light)', fontSize:'1.1rem', flexShrink:0 }}>✓</div>
            )}
          </div>
        ))}
      </div>

      <button className="btn btn-primary w-full" style={{ padding:'1rem', fontSize:'1.1rem' }} onClick={() => setPhase('ready')}>
        Continuer →
      </button>
      <BottomNav />
    </div>
  );

  // ── READY ────────────────────────────────────────────────────────────
  if (phase === 'ready') return (
    <div className="page-content">
      <button className="btn btn-ghost" onClick={() => setPhase('choose')}>← Retour</button>
      <div style={{ textAlign:'center', margin:'1.5rem 0' }}>
        <div style={{ fontSize:'4rem' }}>⚡</div>
        <h2 style={{ margin:'.5rem 0 .3rem' }}>Bible Speedrun</h2>
        <p className="text-small">
          Mode : {correctionMode === 'immediate' ? '⚡ Correction immédiate' : '📝 Résumé final'}
        </p>
      </div>
      <div className="card" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', textAlign:'center', gap:'.75rem', marginBottom:'1.5rem' }}>
        {[['🕐','60s'], ['🔥','+5/combo'], ['💡','Indices']].map(([ic,lb]) => (
          <div key={lb}><div style={{ fontSize:'1.5rem' }}>{ic}</div><div className="text-tiny" style={{ marginTop:'.25rem' }}>{lb}</div></div>
        ))}
      </div>
      <button className="btn btn-primary w-full" style={{ padding:'1rem', fontSize:'1.1rem' }} onClick={start}>🚀 Lancer !</button>
      <BottomNav />
    </div>
  );

  // ── LOADING ──────────────────────────────────────────────────────────
  if (phase === 'loading') return <LoadingScreen />;

  // ── DONE ─────────────────────────────────────────────────────────────
  if (phase === 'done') {
    const correct = history.filter(h => h.ok).length;
    const wrong   = history.filter(h => !h.ok);
    if (!xpRef.current) {
      xpRef.current = true;
      const sessionId = sessionIdRef.current;
      Promise.resolve(onEnd?.({ correct, total: history.length, hintsUsed: hintsUsedRef.current }))
        .then(outcome => {
          if (sessionIdRef.current !== sessionId) return;
          setServerXp(outcome && typeof outcome.xpGained === 'number' ? outcome.xpGained : null);
        })
        .catch(() => { if (sessionIdRef.current === sessionId) setServerXp(null); });
    }

    return (
      <div className="page-content">
        {showScoreCard && (
          <ScoreCard
            gameName="Bible Speedrun" playerName={profile.name || 'Joueur'} playerAvatar={profile.avatar}
            score={`${score} pts`} xp={xpForCard(serverXp)}
            details={[
              { label:'Bonnes réponses', value:`${correct}/${history.length}` },
              { label:'Meilleur combo',  value:`×${maxCombo}` },
              { label:'Précision',       value:`${history.length > 0 ? Math.round((correct/history.length)*100) : 0}%` },
            ]}
            onClose={() => setShowScoreCard(false)}
          />
        )}

        <div style={{ textAlign:'center', marginBottom:'1.25rem' }}>
          <div style={{ fontSize:'3rem' }}>🏁</div>
          <h2 style={{ margin:'.5rem 0' }}>Temps écoulé !</h2>
          {/* Indicateur source — visible uniquement quand des questions API ont été chargées */}
          {apiCount > 0 && (
            <div className="text-tiny" style={{ color:'var(--gold)', marginTop:'.25rem' }}>
              ✦ {apiCount} question{apiCount > 1 ? 's' : ''} depuis la base en ligne
            </div>
          )}
        </div>

        <div className="card-white" style={{ marginBottom:'1rem' }}>
          {[['Bonnes réponses', `${correct}/${history.length}`], ['Score', `${score} pts`], ['Meilleur combo', `×${maxCombo}`], ['XP gagnés', xpLabel(serverXp)]].map(([l,v]) => (
            <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'.55rem 0', borderBottom:'1px solid var(--gray-200)' }}>
              <span style={{ color:'var(--ink-light)' }}>{l}</span>
              <span style={{ fontFamily:'var(--font-display)', fontWeight:700, color:'var(--ink)' }}>{v}</span>
            </div>
          ))}
        </div>

        {/* Révision des erreurs */}
        {wrong.length > 0 && (
          <div className="card" style={{ marginBottom:'1rem' }}>
            <div style={{ fontFamily:'var(--font-display)', color:'var(--gold-light)', marginBottom:'.5rem', fontSize:'.9rem' }}>
              📚 Révision ({wrong.length} erreur{wrong.length > 1 ? 's' : ''})
            </div>
            {wrong.map((w, i) => (
              <div key={i} style={{ padding:'.4rem 0', borderBottom:'1px solid rgba(201,168,76,.1)', fontSize:'.82rem' }}>
                <div style={{ color:'var(--gray-400)', marginBottom:'.15rem' }}>{w.q}</div>
                <div style={{ display:'flex', gap:'.5rem' }}>
                  <span style={{ color:'#e63946' }}>✗ {w.given || '(vide)'}</span>
                  <span style={{ color:'var(--sage-light)' }}>✓ {w.a}</span>
                </div>
                {w.ref && <BibleRef verse={w.ref} />}
              </div>
            ))}
          </div>
        )}

        <div style={{ display:'flex', gap:'.5rem', flexWrap:'wrap' }}>
          <button className="btn btn-primary" onClick={() => setPhase('choose')}>🔄 Rejouer</button>
          <ShareBtn onClick={copyScore} copied={copied} />
          <ScoreImageBtn onClick={() => setShowScoreCard(true)} />
          <button className="btn btn-secondary" onClick={onBack}>← Menu</button>
        </div>
        <BottomNav />
      </div>
    );
  }

  // ── RUNNING ──────────────────────────────────────────────────────────
  const q  = qs[idx % qs.length];
  const tc = timeLeft <= 10 ? '#e63946' : timeLeft <= 20 ? '#ff9f1c' : 'var(--gold-light)';

  return (
    <div className="page-content">
      {paused && <PauseOverlay onResume={() => setPaused(false)} onQuit={onBack} />}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'.6rem' }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'2rem', color:tc, fontWeight:700 }}>{timeLeft}s</div>
        <div style={{ textAlign:'center' }}>
          <div className="text-tiny">COMBO</div>
          <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', color: combo > 0 ? '#ff9f1c' : 'var(--gray-400)' }}>×{combo}</div>
        </div>
        <div style={{ display:'flex', gap:'.4rem', alignItems:'center' }}>
          <div style={{ textAlign:'right' }}>
            <div className="text-tiny">SCORE</div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', color:'var(--gold-light)' }}>{score}</div>
          </div>
          <RulesBtn gameId="speedrun" />
          <button onClick={() => setPaused(true)} style={{ background:'rgba(255,255,255,.08)', border:'1px solid rgba(255,255,255,.1)', borderRadius:6, color:'var(--parch)', cursor:'pointer', padding:'.3rem .5rem', fontSize:'.85rem' }}>⏸</button>
        </div>
      </div>

      <div className="xp-bar-track" style={{ marginBottom:'1rem', height:8 }}>
        <div className="xp-bar-fill" style={{ width:`${(timeLeft/60)*100}%`, background:tc, transition:'width 1s linear, background .3s' }} />
      </div>

      {/* Correction immédiate overlay */}
      <div style={{ position:'relative', overflow:'visible' }}>
        {flash && (
          <div style={{ position:'absolute', top:'-2.5rem', left:'50%', transform:'translateX(-50%)', whiteSpace:'nowrap', background:flash.color, color:'white', borderRadius:8, padding:'.4rem 1.2rem', fontWeight:700, fontSize:'1.3rem', pointerEvents:'none', zIndex:99, animation:'pop .25s ease both' }}>
            {flash.text}
          </div>
        )}
        {showCorrection ? (
          <div className="card-white" style={{ minHeight:110, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', marginBottom:'1rem', textAlign:'center', border:'2px solid var(--crimson)' }}>
            <div style={{ fontSize:'1.5rem', marginBottom:'.3rem' }}>❌</div>
            <p style={{ fontSize:'.85rem', color:'var(--ink-light)', marginBottom:'.25rem' }}>{showCorrection.q}</p>
            <p style={{ color:'var(--crimson)', fontSize:'.85rem' }}>Répondu : <b>{showCorrection.given || '(vide)'}</b></p>
            <p style={{ color:'var(--sage)', fontSize:'.9rem', fontWeight:700 }}>✓ {showCorrection.correct}</p>
            {showCorrection.ref && <BibleRef verse={showCorrection.ref} />}
          </div>
        ) : (
          <>
            <div className="card-white" style={{ minHeight:90, display:'flex', alignItems:'center', justifyContent:'center', marginBottom:'.75rem', textAlign:'center' }}>
              <p style={{ fontFamily:'var(--font-display)', fontSize:'1.05rem', color:'var(--ink)', lineHeight:1.5 }}>{q?.q}</p>
            </div>
            {showHint && q?.hint && <HintBubble hint={q.hint} />}
            {!showHint && q?.hint && <button className="btn btn-ghost" style={{ fontSize:'.8rem', marginBottom:'.5rem' }} onClick={() => { setShowHint(true); hintsUsedRef.current++; }}>💡 Indice <span style={{ color:'var(--crimson)', fontSize:'.7rem' }}>−{HINT_COSTS.speedrun} XP sur le gain</span></button>}
            {q?.type === 'tf' ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.75rem', marginTop:'.5rem' }}>
                <button onClick={() => answer('Vrai')} style={{ padding:'1rem', borderRadius:8, border:'none', background:'var(--sage)', color:'white', fontFamily:'var(--font-display)', fontWeight:700, cursor:'pointer' }}>✓ Vrai</button>
                <button onClick={() => answer('Faux')} style={{ padding:'1rem', borderRadius:8, border:'none', background:'var(--crimson)', color:'white', fontFamily:'var(--font-display)', fontWeight:700, cursor:'pointer' }}>✗ Faux</button>
              </div>
            ) : q?.type === 'mc' || q?.opts ? (
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'.5rem', marginTop:'.5rem' }}>
                {(q.opts || []).map(opt => <button key={opt} className="answer-btn" onClick={() => answer(opt)}>{opt}</button>)}
              </div>
            ) : (
              <div style={{ display:'flex', gap:'.5rem', marginTop:'.5rem' }}>
                <input className="game-input" value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && answer(input)} autoFocus placeholder="Répondre…" />
                <button className="btn btn-primary" onClick={() => answer(input)}>→</button>
              </div>
            )}
          </>
        )}
      </div>
      <BottomNav />
    </div>
  );
};

export default SpeedrunGame;
